import { getAuth } from 'firebase/auth';
import { fetchGuideWithAuth } from '../../../utils/authenticatedFetchGuide';

const DEFAULT_KNOWLEDGE_DOC_LIMIT = 12000;

const isFirebaseStorageUrl = (url = '') => (
  url.includes('storage.googleapis.com/') ||
  url.includes('firebasestorage.googleapis.com/v0/b/')
);

const extractKnowledgeText = async (url) => {
  if (!url) return '';

  const auth = getAuth();
  const currentUser = auth.currentUser;
  const response = isFirebaseStorageUrl(url)
    ? await fetchGuideWithAuth(currentUser, url)
    : await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to load knowledge document: ${response.status}`);
  }

  const rawText = await response.text();

  try {
    const parsed = JSON.parse(rawText);
    if (typeof parsed === 'string') {
      return parsed;
    }
    if (parsed && typeof parsed.content === 'string') {
      return parsed.content;
    }
    if (parsed && typeof parsed.text === 'string') {
      return parsed.text;
    }
  } catch {
    // Not JSON, fall back to the raw response body.
  }

  return rawText;
};

export const normalizeKnowledgeDocuments = (metadata = {}) => {
  const docs = Array.isArray(metadata.knowledgeDocs) ? metadata.knowledgeDocs : [];
  const fallbackDoc = metadata.knowledgeDocUrl
    ? [{
        url: metadata.knowledgeDocUrl,
        name: metadata.knowledgeDocName || 'knowledge-document',
        chars: metadata.knowledgeDocChars || 0,
        uploadedAt: metadata.knowledgeDocUploadedAt || null
      }]
    : [];

  return [...docs, ...fallbackDoc]
    .filter(doc => doc && doc.url)
    .map(doc => ({
      name: doc.name || 'knowledge-document',
      url: doc.url,
      chars: doc.chars || doc.charCount || 0,
      uploadedAt: doc.uploadedAt || null
    }));
};

export const buildKnowledgeContext = async (metadata = {}, options = {}) => {
  const {
    maxDocs = 3,
    maxChars = DEFAULT_KNOWLEDGE_DOC_LIMIT,
    charsPerDoc = 4000,
    fullDocuments = false,
    heading = 'Uploaded knowledge document excerpts:'
  } = options;

  const documents = normalizeKnowledgeDocuments(metadata).slice(0, maxDocs);
  if (documents.length === 0) return '';

  const parts = [];
  let remainingChars = maxChars;

  for (const doc of documents) {
    if (remainingChars <= 0) break;

    const text = await extractKnowledgeText(doc.url);
    if (!text) continue;

    const normalizedText = text.replace(/\r\n/g, '\n').trim();
    const excerptLimit = fullDocuments ? remainingChars : Math.min(charsPerDoc, remainingChars);
    const excerpt = normalizedText.slice(0, excerptLimit);
    if (!excerpt) continue;

    parts.push(`Document: ${doc.name}\n${excerpt}`);
    remainingChars -= excerpt.length;
  }

  if (parts.length === 0) return '';

  return [
    heading,
    ...parts
  ].join('\n\n');
};

const knowledgeDocumentUtils = {
  buildKnowledgeContext,
  normalizeKnowledgeDocuments
};

export default knowledgeDocumentUtils;
